<template>
<!-- eslint-disable max-len -->
<span>
  <transition enter-active-class="animated fadeIn">
    <img
      v-if="showCharacter"
      :src="narratorImg"
      class="character"
      :style="characterStyle"
    >
  </transition>

  <transition enter-active-class="animated fadeIn">
    <!--
      Never use v-html with end-user generated content, because it's a vector for XSS attacks,
      but this is okay here since narrativeOptionalText is only used/set internally.
    -->
    <div
      v-if="!starfighter && showBubble1 && narrativeOptionalText"
      v-html="narrativeOptionalText"
      class="narrative-title"
      :style="narrativeOptionalTextStyle"
    >
    </div>
    <narrator-statement-bubble
      v-else-if="starfighter && showBubble1 && narrativeOptionalText"
      class="narrator-statement-bubble"
      :style="narrativeOptionalTextStyle"
      :character="char"
    >
      <!--
        Never use v-html with end-user generated content, because it's a vector for XSS attacks,
        but this is okay here since narrativeOptionalText is only used/set internally.
        AFAIK, this is the simplest way to support hyperlinks in dialogue, and it also enables any html formatting.
        This is what the following line used to be:
        <p class="narrator-message">{{ narrativeOptionalText }}</p>
      -->
      <p v-html="narrativeOptionalText" class="narrator-message"></p>
    </narrator-statement-bubble>
  </transition>

  <transition enter-active-class="animated fadeIn">
    <narrator-panel
      v-if="showBubble2"
      no-icon
      :next="onComplete"
      :back="(enableBack ? (() => onComplete(true)) : undefined)"
      class="narrator-panel"
    >
      <!--
        Never use v-html with end-user generated content, because it's a vector for XSS attacks,
        but this is okay here since narrativeText is only used/set internally.
        AFAIK, this is the simplest way to support hyperlinks in dialogue, and it also enables any html formatting.
        This is what the following line used to be:
        <paragraphs>{{ narrativeText }}</paragraphs>
      -->
      <p v-html="narrativeText"></p>
    </narrator-panel>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import Paragraphs from '../../ui/display/Paragraphs';
import NarratorStatementBubble from '../ui/NarratorStatementBubble';
import NarratorPanel from '../ui/NarratorPanel';

export default {
  // $emit 'input' on complete
  components: {
    Paragraphs,
    NarratorStatementBubble,
    NarratorPanel
  },
  data() {
    return {
      showCharacter: false,
      showBubble1: false,
      showBubble2: false,
      complete: false
    };
  },
  computed: {
    ...mapGetters([
      'characters',
      'narrative',
      'narrativeIndex',
      'tooltip',
      'lastNarrative',
      'nextNarrative',
      'narrativeOptionalText',
      'narrativeText',
      'narrator',
      'game',
      'native',
      'starfighter'
    ]),
    enableBack() {
      return this.narrative.forceEnableBack || (this.game.enableBack && !(this.narrative.preventEnableBack));
    },
    char() {
      return this.narrative.centerNarrator ? this.narrator : this.characters[this.narrative.centerCharacter];
    },
    imageVariant() {
      let variantName = '';
      if (this.narrative != null && this.char != null
        && this.char.imageVariants && this.char.imageVariants.length > 0
        && this.narrative.centerVariant) {
        variantName = this.narrative.centerVariant;
      }
      if (variantName) {
        const variant = this.char.imageVariants.find(v => v.name === variantName);
        if (variant) {
          return variant.image;
        }
      }
      return null;
    },
    narratorImg() {
      if (this.imageVariant) {
        return this.resolveAssetPath(this.imageVariant, 'characters');
      }
      return this.resolveAssetPath(this.char.imageChallengeIntro, 'characters');
    },
    characterStyle() {
      return {
        left:
          (this.narrative && this.narrative.centerXOffset)
            ? `calc(110px + ${this.narrative.centerXOffset})`
            : '110px',
        bottom:
          (this.narrative && this.narrative.centerYOffset)
            ? this.narrative.centerYOffset
            : '0',
      };
    },
    narrativeOptionalTextStyle() {
      return {
        color: ((this.native && this.narrative.invertBackgroundlessTextColor) ? 'black' : undefined),
        left:
          (this.narrative && this.narrative.centerXOffset)
            ? `calc(400px + ${this.narrative.centerXOffset})`
            : '400px',
        top:
          (this.narrative && this.narrative.centerYOffset)
            ? `calc(25px - ${this.narrative.centerYOffset})`
            : '25px',
      };
    },
  },
  methods: {
    ...mapActions([
      'playSound',
      'playVoice',
      'stopSounds',
      'setSlowZoom'
    ]),
    async show() {
      const { lastNarrative = {}, narrative } = this;
      const narrativeTypeChange = lastNarrative.type !== narrative.type;
      let hasStoppedSounds = false;
      if (narrativeTypeChange ||
        lastNarrative.centerCharacter !== narrative.centerCharacter) {
        if (!hasStoppedSounds) {
          await Vue.nextTick();
          this.stopSounds();
          hasStoppedSounds = true;
        }
        await new Promise(resolve => setTimeout(resolve, 900));
      }
      this.showCharacter = true;
      if (narrativeTypeChange ||
        lastNarrative.optionalText !== narrative.optionalText) {
        if (this.starfighter) {
          if (!hasStoppedSounds) {
            await Vue.nextTick();
            this.stopSounds();
            hasStoppedSounds = true;
          }
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      this.showBubble1 = true;
      if (!hasStoppedSounds) {
        await Vue.nextTick();
        this.stopSounds();
        hasStoppedSounds = true;
      }
      if (this.narrativeOptionalText && this.narrative.optionalTextVoiceLine) {
        await Vue.nextTick();
        this.playVoice({ line: this.narrative.optionalTextVoiceLine });
      }
      if (narrativeTypeChange ||
        lastNarrative.text !== narrative.text) {
        await new Promise(resolve => setTimeout(resolve, 1750));
      }
      this.showBubble2 = true;
      if (this.narrative.textSound) {
        await Vue.nextTick();
        this.playSound({ sound: this.narrative.textSound });
      }
      if (this.narrative.textVoiceLine) {
        await Vue.nextTick();
        this.playVoice({ line: this.narrative.textVoiceLine });
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Complete guard is required to prevent early button press from zooming
      // the next game state
      if (!this.complete) this.setSlowZoom({ slowZoom: true });
    },
    async reset() {
      this.complete = false;
      await this.show();
    },
    async hide() {
      const { narrative, nextNarrative = {} } = this;
      const narrativeTypeChange = narrative.type !== nextNarrative.type;
      if (narrativeTypeChange ||
        narrative.centerCharacter !== nextNarrative.centerCharacter) {
        this.showCharacter = false;
      }
      if (narrativeTypeChange ||
        narrative.optionalText !== nextNarrative.optionalText) {
        this.showBubble1 = false;
      }
      if (narrativeTypeChange ||
        narrative.text !== nextNarrative.text) {
        this.showBubble2 = false;
      }
      this.setSlowZoom({ slowZoom: false });
      await Vue.nextTick();
      this.stopSounds();
    },
    async onComplete(back = false) {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        window.dispatchEvent(new Event('click'));
        return;
      }
      this.complete = true;
      await this.hide();
      this.$emit('input', back);
    }
  },
  watch: {
    async narrative(newValue) {
      if (newValue) await this.reset();
    }
  },
  async mounted() {
    await this.show();
  },
  async beforeDestroy() {
    this.setSlowZoom({ slowZoom: false });
  }
};
</script>

<style lang="scss" scoped>
.character {
  position: absolute;
  bottom: 0;
  left: 110px;
  transform-origin: bottom left;
}
.narrator-panel {
  bottom: 15px;
  left: 30px;
}
.narrative-title {
  position: absolute;
  top: 30px;
  left: 0;
  width: 100%;
  height: auto;
  font-size: 50px;
  line-height: 55px;
  margin: 0;
  text-align: center;
  vertical-align: top;
  color: white;
}
.narrator-statement-bubble {
  top: 25px;
  left: 400px;
  height: auto;
}
.narrator-message {
  font-size: 48px;
  line-height: 60px;
  margin: 0;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeAnimation.vue